/**
 * Framework-free DM attachment limits + validation, shared by the client and
 * town-square upload helpers. Images (JPEG/PNG) go through the image pipeline;
 * files (PDF, docs, spreadsheets) are stored as-is in the private bucket.
 * Proven in shared/test/dm-attachments.spec.ts.
 */

export const ALLOWED_DM_ATTACHMENT_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png']);
export const MAX_DM_ATTACHMENT_BYTES = 10 * 1024 * 1024;
export const MAX_DM_ATTACHMENTS = 10;

/** Non-image files a DM may carry — documents people actually send each other. */
export const ALLOWED_DM_FILE_TYPES = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/csv',
  'text/plain',
  'application/zip',
]);
/** Extension fallback for browsers that report an empty/generic MIME type (CSV, zip). */
export const ALLOWED_DM_FILE_EXTENSIONS = new Set([
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'csv',
  'txt',
  'zip',
]);
export const MAX_DM_FILE_BYTES = 25 * 1024 * 1024;

/** `accept` attribute for the DM file picker — images plus the document set. */
export const DM_ATTACHMENT_ACCEPT =
  'image/png,image/jpeg,application/pdf,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.zip';

/** Minimal shape of a browser File — kept DOM-free so shared can be spec'd with plain objects. */
export interface AttachableFile {
  type: string;
  name: string;
  size: number;
}

/** Canonical MIME type per document extension — for browsers that report none. */
export const DM_FILE_EXTENSION_TO_TYPE: Record<string, string> = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  csv: 'text/csv',
  txt: 'text/plain',
  zip: 'application/zip',
};

function extensionOf(name: string): string {
  return /\.([a-z0-9]+)$/i.exec(name)?.[1]?.toLowerCase() ?? '';
}

function isHeicName(name: string): boolean {
  return /\.(heic|heif)$/i.test(name);
}

/** JPEG/PNG (not HEIC), ignoring the size cap. */
function isAllowedDmImageType(file: AttachableFile): boolean {
  return ALLOWED_DM_ATTACHMENT_TYPES.has(file.type) && !isHeicName(file.name);
}

/** Allowed document type, including the empty/generic MIME extension fallback. */
function isAllowedDmFileType(file: AttachableFile): boolean {
  if (ALLOWED_DM_FILE_TYPES.has(file.type)) return true;
  const generic = file.type === '' || file.type === 'application/octet-stream';
  return generic && ALLOWED_DM_FILE_EXTENSIONS.has(extensionOf(file.name));
}

/** Allowed image or document whose byte size is over its cap. */
function isOversizedAllowedAttachment(file: AttachableFile): boolean {
  if (!Number.isFinite(file.size)) return false;
  if (isAllowedDmImageType(file) && file.size > MAX_DM_ATTACHMENT_BYTES) return true;
  if (isAllowedDmFileType(file) && file.size > MAX_DM_FILE_BYTES) return true;
  return false;
}

/** The media type to store/sign for a file — the browser's, or one keyed off the extension. */
export function dmFileMediaType(file: AttachableFile): string {
  if (file.type && file.type !== 'application/octet-stream') return file.type;
  return DM_FILE_EXTENSION_TO_TYPE[extensionOf(file.name)] || file.type || 'application/octet-stream';
}

/** A DM image attachment must be a JPEG/PNG (not HEIC) within the image size cap. */
export function isAllowedDmAttachment(file: AttachableFile): boolean {
  return isAllowedDmImageType(file) && file.size <= MAX_DM_ATTACHMENT_BYTES;
}

/** A DM file attachment must be an allowed document type within the file size cap. */
export function isAllowedDmFile(file: AttachableFile): boolean {
  if (!Number.isFinite(file.size) || file.size < 0 || file.size > MAX_DM_FILE_BYTES) return false;
  return isAllowedDmFileType(file);
}

/**
 * Split picker/paste/drop files into images, files, type-rejected, and oversized.
 * Oversized allowed types (a 30 MB PDF, a 20 MB PNG) stay out of `rejected` so
 * the composer can toast a size limit instead of an unsupported-type message.
 */
export function classifyDmAttachments<T extends AttachableFile>(
  files: readonly T[]
): {
  images: T[];
  files: T[];
  rejected: T[];
  oversized: T[];
} {
  const images: T[] = [];
  const docs: T[] = [];
  const rejected: T[] = [];
  const oversized: T[] = [];
  for (const file of files) {
    if (isAllowedDmAttachment(file)) images.push(file);
    else if (isAllowedDmFile(file)) docs.push(file);
    else if (isOversizedAllowedAttachment(file)) oversized.push(file);
    else rejected.push(file);
  }
  return { images, files: docs, rejected, oversized };
}

/** Split picker/paste/drop files into uploadable images vs toast-rejected. */
export function partitionDmAttachments<T extends AttachableFile>(
  files: readonly T[]
): {
  accepted: T[];
  rejected: T[];
} {
  const accepted: T[] = [];
  const rejected: T[] = [];
  for (const file of files) {
    if (isAllowedDmAttachment(file)) accepted.push(file);
    else rejected.push(file);
  }
  return { accepted, rejected };
}
